import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {Util} from '../util/util';

/**
 * Synchronizes the backend-managed HttpOnly awp_cookie with the current OIDC access token.
 */
@Injectable({
  providedIn: 'root'
})
export class AwpCookieService {
  private readonly httpClient: HttpClient = inject(HttpClient);

  /** Cookie API endpoint exposed by the gateway. */
  private readonly cookieApiUrl: string = '/api/user/cookie/v1';

  /** Allows the cookie call to bypass WenInterceptor header enrichment. */
  private readonly skipInterceptorHeaderName: string = 'x-skip-token-interceptor';

  /** Retry delay for recoverable cookie synchronization errors. */
  private readonly retryDelayMs: number = 250;

  /**
   * Creates the awp_cookie after initial login token acquisition.
   */
  createCookie(accessToken: string): Promise<void> {
    console.info(this.logPrefix() + 'Creating awp_cookie after initial token acquisition.');
    return this.callCookieApiWithRetry('POST', accessToken);
  }

  /**
   * Refreshes the awp_cookie whenever the OIDC access token changes.
   */
  refreshCookie(accessToken: string): Promise<void> {
    console.info(this.logPrefix() + 'Refreshing awp_cookie because OIDC access token changed.');
    return this.callCookieApiWithRetry('PUT', accessToken);
  }

  /**
   * Deletes the awp_cookie during logout. This method is best effort.
   */
  deleteCookie(accessToken?: string): Promise<void> {
    console.info(this.logPrefix() + 'Deleting awp_cookie during logout (best effort).');
    return this.callCookieApi('DELETE', accessToken);
  }

  /**
   * Executes cookie create/refresh with one retry and short backoff.
   */
  private async callCookieApiWithRetry(method: 'POST' | 'PUT', accessToken: string): Promise<void> {
    try {
      await this.callCookieApi(method, accessToken);
    } catch (error: unknown) {
      console.warn(this.logPrefix()
        + `Cookie ${this.operationName(method)} call failed on first attempt. Retrying once in ${this.retryDelayMs}ms.`,
        error);
      await this.wait(this.retryDelayMs);
      await this.callCookieApi(method, accessToken);
      console.info(this.logPrefix() + `Cookie ${this.operationName(method)} succeeded on retry.`);
    }
  }

  /**
   * Performs one cookie API request with credentials and required headers.
   */
  private async callCookieApi(method: 'POST' | 'PUT' | 'DELETE', accessToken?: string): Promise<void> {
    const operationName: string = this.operationName(method);
    console.debug(this.logPrefix()
      + `Sending cookie ${operationName} request with session-id=${!!sessionStorage.getItem('cavors-session-id')}.`);
    const headers: HttpHeaders = this.buildCookieHeaders(accessToken);
    const request$: Observable<unknown> = this.httpClient.request(method, this.cookieApiUrl, {
      headers,
      withCredentials: true
    });
    await firstValueFrom(request$);
    console.info(this.logPrefix() + `Cookie ${operationName} request succeeded.`);
  }

  /**
   * Maps HTTP methods to domain-specific cookie lifecycle actions for logs.
   */
  private operationName(method: 'POST' | 'PUT' | 'DELETE'): 'create' | 'refresh' | 'delete' {
    if (method === 'POST') {
      return 'create';
    }
    if (method === 'PUT') {
      return 'refresh';
    }
    return 'delete';
  }

  /**
   * Returns a consistent logger prefix for cookie lifecycle messages.
   */
  private logPrefix(): string {
    return Util.logPrefix('Webapp', 'Cookie');
  }

  /**
   * Builds the required backend headers for cookie lifecycle endpoints.
   */
  private buildCookieHeaders(accessToken?: string): HttpHeaders {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.set(this.skipInterceptorHeaderName, 'true');
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('cavors-request-id', Util.createRandomUuid());

    const cavorsSessionId: string | null = sessionStorage.getItem('cavors-session-id');
    if (cavorsSessionId) {
      headers = headers.set('cavors-session-id', cavorsSessionId);
    }

    if (accessToken) {
      headers = headers.set('authorization', `Bearer ${accessToken}`);
      headers = headers.set('access_token', `${accessToken}`);
    }

    return headers;
  }

  /**
   * Wait helper used for short retry backoff.
   */
  private wait(timeoutMs: number): Promise<void> {
    return new Promise<void>((resolve: () => void): void => {
      window.setTimeout((): void => resolve(), timeoutMs);
    });
  }
}

